import useFetchAndTrace, { FetchAndTraceErrorHandler } from "../fetchWithTrace";
import { useAppDispatch, useAppSelector } from "../store";
import {
  PickupInfo,
  setCartActionError,
  setNotFound,
  setShoppingCart,
  setUpdatingShoppingCart,
  ShippingRate,
  ShoppingCartOrder,
} from "../../../reducers/shoppingCartOrder";
import settings from "../../../constants/settings";
import { useCallback, useEffect } from "react";
import { ShippingAddress } from "../../../reducers/uploadedBoard";
import { isCouponAppliedToCart } from "../../shoppingCart";
import { Coupon } from "../../../reducers/models/users";
import useTrackGoogleEvent, {
  PURCHASE_EVENT,
} from "../../observability/googleTagManager";
import useSWR from "swr";
import useSWRFetcher, { SWRFetchError } from "./fetcher";
import { ShoppingCartSummary } from "../../../reducers/models/shoppingCartSummary";
import { trackOrderPlaced } from "../../observability/boardTracking";

export const useShoppingCartSummary = () => {
  const { signedIn, userId } = useAppSelector((state) => state.auth);
  const fetcher = useSWRFetcher<ShoppingCartSummary>();

  const { data, mutate } = useSWR(
    signedIn
      ? `${settings.api_url}/users/${userId}/shopping-cart/summary`
      : null,
    fetcher,
  );

  return {
    shoppingCartSummary: data || null,
    refreshShoppingCartSummary: mutate,
  };
};

export interface ShoppingCartOrderEndpoint {
  type: "cart" | "order";
  orderId?: string;
  userId: string;
  retryOnNotFound?: boolean;
}

export const useFetchShoppingCartOrder = (
  endpoint: ShoppingCartOrderEndpoint,
) => {
  const fetchAndTrace = useFetchAndTrace();
  const dispatch = useAppDispatch();
  const { signedIn } = useAppSelector((state) => state.auth);
  const fetcher = useSWRFetcher<ShoppingCartOrder>(undefined);
  const trackGoogleEvent = useTrackGoogleEvent();

  const getPath = () => {
    if (!signedIn) {
      return null;
    }
    if (endpoint.type == "cart") {
      return `${settings.api_url}/users/${endpoint.userId}/shopping-cart`;
    } else {
      return `${settings.api_url}/orders/${endpoint.orderId as string}`;
    }
  };

  const { data, mutate, error } = useSWR<ShoppingCartOrder, SWRFetchError>(
    getPath(),
    fetcher,
    {
      onErrorRetry: (error, key, config, revalidate) => {
        if (error.status == 404) {
          setTimeout(() => void revalidate(), 1000);
        }
      },
    },
  );

  useEffect(() => {
    if (error !== undefined) {
      if (error.status == 404) {
        dispatch(setNotFound());
      }
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (data !== undefined)
      dispatch(
        setShoppingCart({
          shoppingCartOrder: data,
        }),
      );
  }, [data, dispatch, error]);

  const markTrackedHandler = useCallback(
    (response: Response) => {
      if (response.ok) {
        void mutate({
          ...(data as ShoppingCartOrder),
          was_tracked: true,
        });
      } else {
        throw Error("Failed to mark order as tracked");
      }
    },
    [data, mutate],
  );

  useEffect(() => {
    if (endpoint.type == "order" && data !== undefined && !data.was_tracked) {
      trackOrderPlaced(data);
      trackGoogleEvent({
        action: PURCHASE_EVENT,
        parameters: {
          value: data.order_total,
          currencyCode: data.currency,
          orderId: endpoint.orderId as string,
        },
      });
      void fetchAndTrace(
        `${settings.api_url}/orders/${endpoint.orderId as string}/tracked`,
        markTrackedHandler,
        null,
        { method: "PUT" },
      );
    }
  }, [
    data,
    endpoint.orderId,
    endpoint.type,
    fetchAndTrace,
    markTrackedHandler,
    trackGoogleEvent,
  ]);

  useEffect(() => {
    return () => {
      void mutate();
    };
  }, [mutate]);

  return mutate;
};

export const useUpdateShoppingCart = (userId: string) => {
  const fetchAndTrace = useFetchAndTrace();
  const dispatch = useAppDispatch();
  const errorHandler: FetchAndTraceErrorHandler = useCallback(() => {
    dispatch(setCartActionError({ errored: true }));
  }, [dispatch]);
  const { shoppingCartOrder: shoppingCart } = useAppSelector(
    (state) => state.shoppingCartOrder,
  ) as {
    shoppingCartOrder: ShoppingCartOrder;
  };

  const updateShoppingCartHandler = useCallback(
    async (response: Response) => {
      if (response.ok) {
        dispatch(setUpdatingShoppingCart({ updating: false }));
        dispatch(
          setShoppingCart({
            shoppingCartOrder: (await response.json()) as ShoppingCartOrder,
          }),
        );
      } else {
        throw Error("Failed to update shopping cart");
      }
    },
    [dispatch],
  );

  const updateShoppingCart = useCallback(
    (shoppingCart: ShoppingCartOrder) => {
      dispatch(setUpdatingShoppingCart({ updating: true }));
      dispatch(setShoppingCart({ shoppingCartOrder: shoppingCart }));
      void fetchAndTrace(
        `${settings.api_url}/users/${userId}/shopping-cart`,
        updateShoppingCartHandler,
        errorHandler,
        {
          body: JSON.stringify(shoppingCart),
          method: "PUT",
        },
      );
    },
    [dispatch, errorHandler, fetchAndTrace, updateShoppingCartHandler, userId],
  );

  const clearDeliveryOptions = useCallback(() => {
    updateShoppingCart({
      ...shoppingCart,
      pickup_info: null,
      shipping_address: null,
      shipping_rate: null,
    });
  }, [shoppingCart, updateShoppingCart]);

  const addShippingAddress = useCallback(
    (address: ShippingAddress) => {
      updateShoppingCart({
        ...shoppingCart,
        pickup_info: null,
        shipping_rate: null,
        shipping_address: address,
      });
    },
    [shoppingCart, updateShoppingCart],
  );

  const addShippingRate = useCallback(
    (rate: ShippingRate) => {
      updateShoppingCart({
        ...shoppingCart,
        shipping_rate: rate,
      });
    },
    [shoppingCart, updateShoppingCart],
  );

  const addPickupInfo = useCallback(
    (pickupInfo: PickupInfo) => {
      updateShoppingCart({
        ...shoppingCart,
        shipping_address: null,
        shipping_rate: null,
        pickup_info: pickupInfo,
      });
    },
    [shoppingCart, updateShoppingCart],
  );

  const toggleCoupon = useCallback(
    (coupon: Coupon) => {
      const couponApplied = isCouponAppliedToCart(coupon, shoppingCart);
      if (couponApplied) {
        updateShoppingCart({
          ...shoppingCart,
          coupons_applied: shoppingCart.coupons_applied.filter(
            (appliedCoupon) => appliedCoupon.id != coupon.id,
          ),
        });
      } else {
        updateShoppingCart({
          ...shoppingCart,
          coupons_applied: [coupon, ...shoppingCart.coupons_applied],
        });
      }
    },
    [shoppingCart, updateShoppingCart],
  );

  return {
    clearDeliveryOptions,
    addShippingAddress,
    addShippingRate,
    addPickupInfo,
    toggleCoupon,
  };
};
